import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaArrowRight,
  FaPhoneAlt,
  FaRobot,
  FaUser,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { parseUtc } from '../utils/dates';
import {
  outcomeChips,
  formatCallFull,
  formatCallTime,
  formatDuration,
  callerInitials,
  TOOL_META,
} from '../utils/voiceFormat';
import './VoiceTickets.css';
import './VoiceTicketDetail.css';

// A tool call's arguments as a short human line: known keys first, then up to
// three leftover primitives — never raw JSON in the timeline.
const summarizeArgs = (args, t) => {
  if (!args || typeof args !== 'object') return '';
  const parts = [];
  const date = parseUtc(args.date);
  if (date) parts.push(date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'short' }));
  if (args.time) parts.push(String(args.time));
  if (args.people != null) parts.push(t('voiceTickets.detail.peopleShort', '{{n}} pers.', { n: args.people }));
  const known = new Set(['date', 'time', 'people']);
  Object.entries(args)
    .filter(([key, value]) => !known.has(key) && (typeof value === 'string' || typeof value === 'number'))
    .slice(0, 3)
    .forEach(([key, value]) => parts.push(`${key} : ${String(value).slice(0, 40)}`));
  return parts.join(' · ');
};

const EventCard = ({ call, t }) => {
  const [open, setOpen] = useState(false);
  const meta = TOOL_META[call.name];
  const kind = meta?.kind || 'lookup';
  if (kind === 'ignore') return null;
  const label = meta?.key
    ? t(`voiceTickets.outcomes.${meta.key}`, meta.fallback)
    : String(call.name || '').replace(/_/g, ' ');
  const summary = summarizeArgs(call.arguments, t);
  return (
    <div className="vtd-event">
      <button type="button" className={`vtd-event-card ${kind}`} onClick={() => setOpen(!open)}>
        <span className={`vtd-dot ${kind}`} aria-hidden="true" />
        <b>{label}</b>
        {summary && <small>{summary}</small>}
        <small className="vtd-event-toggle">{open ? t('voiceTickets.detail.hideDetails', 'masquer') : t('voiceTickets.detail.details', 'détails')}</small>
      </button>
      {open && (
        <div className="vtd-event-details">
          {call.arguments && Object.keys(call.arguments).length > 0 && (
            <pre>{JSON.stringify(call.arguments, null, 2)}</pre>
          )}
          {call.result && <p>{String(call.result)}</p>}
        </div>
      )}
    </div>
  );
};

const VoiceTicketDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { callId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [memory, setMemory] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchConversation = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/ai-voice/conversations/${encodeURIComponent(callId)}`, { headers });
      const data = response.data || null;
      setConversation(data);

      if (data?.phone_number) {
        // Profile + the caller's other calls (prev/next navigation), best effort.
        const [memoryResult, siblingsResult] = await Promise.allSettled([
          axios.get(`/api/ai-voice/memory?phone_number=${encodeURIComponent(data.phone_number)}`, { headers }),
          axios.get(`/api/ai-voice/conversations?phone_number=${encodeURIComponent(data.phone_number)}&limit=200`, { headers }),
        ]);
        setMemory(memoryResult.status === 'fulfilled' ? memoryResult.value.data || null : null);
        setSiblings(siblingsResult.status === 'fulfilled' ? siblingsResult.value.data?.conversations || [] : []);
      }
    } catch (err) {
      console.error('Failed to fetch voice conversation:', err);
      setError(err?.response?.data?.detail || t('voiceTickets.detail.fetchError', 'Échec du chargement de la conversation.'));
    } finally {
      setLoading(false);
    }
  }, [callId, headers, t]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  // Transcript turns and tool events merged into one chronological timeline.
  const timeline = useMemo(() => {
    if (!conversation) return [];
    const items = [];
    (conversation.transcript || []).forEach((turn, index) => {
      items.push({ type: 'turn', ts: parseUtc(turn.ts)?.getTime() ?? index, turn, key: `turn-${index}` });
    });
    (conversation.tool_calls || []).forEach((call, index) => {
      const ts = parseUtc(call.ts)?.getTime();
      items.push({ type: 'event', ts: ts ?? Number.MAX_SAFE_INTEGER - index, call, key: `event-${index}` });
    });
    return items.sort((a, b) => a.ts - b.ts);
  }, [conversation]);

  const { prevCall, nextCall } = useMemo(() => {
    const index = siblings.findIndex((conv) => conv.call_id === callId);
    if (index === -1) return { prevCall: null, nextCall: null };
    // siblings are sorted newest first: "previous call" = the older one.
    return { prevCall: siblings[index + 1] || null, nextCall: siblings[index - 1] || null };
  }, [siblings, callId]);

  if (loading) {
    return (
      <div className="vtd-page">
        <div className="vtd-state">{t('voiceTickets.detail.loading', 'Chargement de la conversation…')}</div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="vtd-page">
        <button type="button" className="vtd-back" onClick={() => navigate('/voice-tickets')}>
          <FaArrowLeft /> {t('voiceTickets.detail.back', 'Retour aux tickets vocaux')}
        </button>
        <div className="vt-alert">{error || t('voiceTickets.detail.notFound', 'Conversation introuvable.')}</div>
      </div>
    );
  }

  const callerName = conversation.caller_name || memory?.caller_name || null;
  const chips = outcomeChips(conversation.tool_names || (conversation.tool_calls || []).map((c) => c.name), t, { includeLookups: true });
  const lineLabel = conversation.assistant_name
    ? `${t('voiceTickets.detail.line', 'Ligne')} ${conversation.assistant_name} · ${conversation.dialed_number || ''}`.trim()
    : `${t('voiceTickets.detail.line', 'Ligne')} ${conversation.dialed_number || '—'}`;

  return (
    <div className="vtd-page">
      <button type="button" className="vtd-back" onClick={() => navigate('/voice-tickets')}>
        <FaArrowLeft /> {t('voiceTickets.detail.back', 'Retour aux tickets vocaux')}
      </button>

      <header className="vtd-head">
        <span className="vt-avatar lg" aria-hidden="true">{callerInitials(callerName, conversation.phone_number)}</span>
        <div className="vtd-head-main">
          <h1>{callerName || conversation.phone_number || t('voiceTickets.detail.unknownCaller', 'Appelant inconnu')}</h1>
          <div className="vtd-head-meta">
            {callerName && <span className="vt-chip neutral vt-num"><FaPhoneAlt aria-hidden="true" /> {conversation.phone_number}</span>}
            {conversation.client_id && (
              <button type="button" className="vt-chip accent" onClick={() => navigate(`/clients/${conversation.client_id}`)}>
                {t('voiceTickets.detail.clientLink', 'Fiche client')} <FaExternalLinkAlt aria-hidden="true" />
              </button>
            )}
            <span className="vt-chip line">{lineLabel}</span>
            <span className="vt-chip neutral vt-num">
              {formatCallFull(conversation.started_at)} · {formatDuration(conversation.duration_seconds)} · {t('voiceTickets.turnsCount', '{{n}} échanges', { n: (conversation.transcript || []).length })}
            </span>
          </div>
        </div>
        <div className="vtd-nav">
          <button type="button" className="vt-btn" disabled={!prevCall} onClick={() => prevCall && navigate(`/voice-tickets/${encodeURIComponent(prevCall.call_id)}`)}>
            <FaArrowLeft /> {t('voiceTickets.detail.prevCall', 'Appel préc.')}
          </button>
          <button type="button" className="vt-btn" disabled={!nextCall} onClick={() => nextCall && navigate(`/voice-tickets/${encodeURIComponent(nextCall.call_id)}`)}>
            {t('voiceTickets.detail.nextCall', 'Appel suiv.')} <FaArrowRight />
          </button>
        </div>
      </header>

      <div className="vtd-cols">
        <section className="vtd-panel">
          <h3>{t('voiceTickets.detail.conversation', 'Conversation')}</h3>
          {timeline.length === 0 ? (
            <div className="vtd-state">{t('voiceTickets.detail.noTranscript', 'Aucune transcription disponible pour cet appel.')}</div>
          ) : (
            <div className="vtd-timeline">
              {timeline.map((item) => (
                item.type === 'event' ? (
                  <EventCard key={item.key} call={item.call} t={t} />
                ) : (
                  <div key={item.key} className={`vtd-turn ${item.turn.role === 'caller' ? 'caller' : 'agent'}`}>
                    <span className={`vtd-mini ${item.turn.role === 'caller' ? 'caller' : 'bot'}`} aria-hidden="true">
                      {item.turn.role === 'caller' ? <FaUser /> : <FaRobot />}
                    </span>
                    <div className="vtd-bubble">
                      {item.turn.text}
                      {item.turn.ts && <time>{formatCallTime(item.turn.ts)}</time>}
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </section>

        <aside className="vtd-rail">
          <div className="vtd-panel">
            <h3>{t('voiceTickets.detail.thisCall', 'Cet appel')}</h3>
            {chips.length > 0 && (
              <div className="vt-chips wrap">
                {chips.map((chip) => (
                  <span key={chip.name} className={`vt-chip ${chip.kind}`}>{chip.label}</span>
                ))}
              </div>
            )}
            {conversation.assistant_name && (
              <div className="vtd-fact"><span>{t('voiceTickets.detail.voiceAgent', 'Agent vocal')}</span><b>{conversation.assistant_name}</b></div>
            )}
            <div className="vtd-fact"><span>{t('voiceTickets.detail.dialedLine', 'Ligne appelée')}</span><b className="vt-num">{conversation.dialed_number || '—'}</b></div>
            <div className="vtd-fact">
              <span>{t('voiceTickets.duration', 'Durée')}</span>
              <b className="vt-num">{formatDuration(conversation.duration_seconds)} · {t('voiceTickets.turnsCount', '{{n}} échanges', { n: (conversation.transcript || []).length })}</b>
            </div>
            <div className="vtd-fact"><span>{t('voiceTickets.detail.date', 'Date')}</span><b className="vt-num">{formatCallFull(conversation.started_at)}</b></div>
          </div>

          <div className="vtd-panel">
            <h3>{t('voiceTickets.detail.callerProfile', 'Profil de l’appelant')}</h3>
            {!memory ? (
              <div className="vtd-state">{t('voiceTickets.detail.noMemory', 'Aucun profil mémoire pour cet appelant pour le moment.')}</div>
            ) : (
              <>
                {memory.caller_name && (
                  <div className="vtd-fact"><span>{t('voiceTickets.detail.name', 'Nom')}</span><b>{memory.caller_name}</b></div>
                )}
                {memory.email && (
                  <div className="vtd-fact"><span>{t('voiceTickets.detail.email', 'E-mail')}</span><b>{memory.email}</b></div>
                )}
                {memory.postal_address && (
                  <div className="vtd-fact"><span>{t('voiceTickets.detail.address', 'Adresse')}</span><b>{memory.postal_address}</b></div>
                )}
                <div className="vtd-fact">
                  <span>{t('voiceTickets.detail.callCount', 'Appels')}</span>
                  <b className="vt-num">{t('voiceTickets.detail.callsTotal', '{{n}} au total', { n: memory.call_count ?? 0 })}</b>
                </div>
                {memory.summary && (
                  <>
                    <div className="vtd-divider" />
                    <p className="vtd-profile-sum">{memory.summary}</p>
                  </>
                )}
                {Array.isArray(memory.preferences) && memory.preferences.length > 0 && (
                  <div className="vt-chips wrap">
                    {memory.preferences.map((pref, index) => (
                      <span key={index} className="vt-chip neutral">{pref}</span>
                    ))}
                  </div>
                )}
                {Array.isArray(memory.past_requests) && memory.past_requests.length > 0 && (
                  <div className="vtd-pastreqs">
                    {memory.past_requests.slice(-6).reverse().map((req, index) => {
                      const isObject = typeof req === 'object' && req !== null;
                      const date = isObject ? parseUtc(req.date) : null;
                      return (
                        <div key={index} className="vtd-pastreq">
                          {date && <time>{date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'short', year: 'numeric' })}</time>}
                          {isObject ? `${req.request || ''}${req.outcome ? ` (${req.outcome})` : ''}`.trim() : String(req)}
                        </div>
                      );
                    })}
                  </div>
                )}
                {conversation.client_id && (
                  <button type="button" className="vtd-cta" onClick={() => navigate(`/clients/${conversation.client_id}`)}>
                    {t('voiceTickets.detail.viewClient', 'Voir la fiche client')} →
                  </button>
                )}
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default VoiceTicketDetail;
